import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TournamentOperatorPlaybook } from "@/libs/tournamentApi";

const stepKeys = [
  "setup",
  "registration",
  "competition",
  "operations",
  "closeout",
  "archive",
] as const;
const tabDestinations = [
  "overview",
  "events",
  "entries",
  "draws",
  "schedule",
  "closeout",
  "reports",
] as const;
const destinationLabels = {
  overview: "Overview",
  events: "Events",
  entries: "Entries",
  draws: "Draws",
  schedule: "Schedule",
  control_center: "Control Center",
  closeout: "Closeout",
  reports: "Reports",
} as const;

type TabDestination = (typeof tabDestinations)[number];
type PlaybookDestination = TabDestination | "control_center";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPlaybookDestination(value: unknown): value is PlaybookDestination {
  return (
    value === "control_center" ||
    (tabDestinations as readonly string[]).includes(String(value))
  );
}

function normalizedPlaybook(
  value: unknown,
  tournamentId: string,
): TournamentOperatorPlaybook | null {
  if (
    !isRecord(value) ||
    value.tournament_id !== tournamentId ||
    !isRecord(value.lifecycle) ||
    typeof value.lifecycle.current_phase !== "string" ||
    typeof value.lifecycle.current_phase_label !== "string" ||
    !Array.isArray(value.steps) ||
    value.steps.length !== stepKeys.length
  ) {
    return null;
  }
  const currentIndex = value.steps.findIndex(
    (step) => isRecord(step) && step.state === "current",
  );
  if (currentIndex < 0) return null;
  const validSteps = value.steps.every((step, index) => {
    if (!isRecord(step)) return false;
    const expectedState =
      index < currentIndex
        ? "complete"
        : index === currentIndex
          ? "current"
          : "upcoming";
    return (
      step.key === stepKeys[index] &&
      step.state === expectedState &&
      typeof step.label === "string" &&
      typeof step.description === "string" &&
      isPlaybookDestination(step.destination)
    );
  });
  if (!validSteps) return null;

  if (currentIndex !== 0) {
    return value.publish_readiness === null
      ? (value as TournamentOperatorPlaybook)
      : null;
  }
  if (!isRecord(value.publish_readiness)) return null;
  const readiness = value.publish_readiness;
  if (
    readiness.tournament_id !== tournamentId ||
    typeof readiness.is_ready !== "boolean" ||
    !Array.isArray(readiness.checks)
  ) {
    return null;
  }
  if (
    !readiness.checks.every(
      (check) =>
        isRecord(check) &&
        typeof check.label === "string" &&
        typeof check.is_complete === "boolean" &&
        typeof check.required === "boolean" &&
        (check.message === null || typeof check.message === "string") &&
        (check.destination === "overview" || check.destination === "events"),
    )
  ) {
    return null;
  }
  return value as TournamentOperatorPlaybook;
}

function playbookStateLabel(state: "complete" | "current" | "upcoming") {
  return state === "complete"
    ? "Complete"
    : state === "current"
      ? "Current"
      : "Upcoming";
}

export function TournamentOperatorPlaybook({
  onNavigateToControlCenter,
  onNavigateToTab,
  playbook,
  tournamentId,
}: {
  onNavigateToControlCenter: () => void;
  onNavigateToTab: (destination: TabDestination) => void;
  playbook: unknown;
  tournamentId: string;
}) {
  const value = normalizedPlaybook(playbook, tournamentId);

  if (!value) {
    return (
      <Alert severity="warning">
        Operator guidance is unavailable for this tournament.
      </Alert>
    );
  }

  const currentStep = value.steps.find((step) => step.state === "current");
  if (!currentStep) {
    return (
      <Alert severity="warning">
        Operator guidance is unavailable for this tournament.
      </Alert>
    );
  }
  const openDestination = (destination: PlaybookDestination) => {
    if (destination === "control_center") {
      onNavigateToControlCenter();
      return;
    }
    onNavigateToTab(destination);
  };
  const publishChecks = value.publish_readiness?.checks ?? [];

  return (
    <Box component="section" sx={{ minWidth: 0 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{ fontWeight: 700 }}
              variant="subtitle1"
            >
              Operator playbook
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {value.lifecycle.current_phase_label}
            </Typography>
          </Box>
          <Chip label={playbookStateLabel(currentStep.state)} size="small" />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
        >
          <Typography sx={{ flex: 1 }} variant="body2">
            {currentStep.description}
          </Typography>
          <Button
            onClick={() => openDestination(currentStep.destination)}
            size="small"
            variant="contained"
          >
            Open {destinationLabels[currentStep.destination]}
          </Button>
        </Stack>

        {value.publish_readiness ? (
          <>
            <Divider />
            <Stack spacing={1}>
              <Typography
                component="h3"
                sx={{ fontWeight: 700 }}
                variant="subtitle2"
              >
                Before publishing
              </Typography>
              {publishChecks.map((check) => (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  key={check.key}
                  spacing={1}
                  sx={{
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{check.label}</Typography>
                    {!check.is_complete && check.message ? (
                      <Typography color="text.secondary" variant="body2">
                        {check.message}
                      </Typography>
                    ) : null}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Chip
                      color={
                        check.is_complete
                          ? "success"
                          : check.required
                            ? "error"
                            : "default"
                      }
                      label={
                        check.is_complete
                          ? "Done"
                          : check.required
                            ? "Required"
                            : "Optional"
                      }
                      size="small"
                    />
                    <Button
                      onClick={() => openDestination(check.destination)}
                      size="small"
                      variant="outlined"
                    >
                      Open {destinationLabels[check.destination]}
                    </Button>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </>
        ) : null}

        <Divider />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {value.steps.map((step) => (
            <Chip
              color={step.state === "current" ? "primary" : "default"}
              key={step.key}
              label={`${step.label}: ${playbookStateLabel(step.state)}`}
              size="small"
              variant={step.state === "current" ? "filled" : "outlined"}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
